import { useEffect, useState } from "react";
import { ApiError, cabinetApi, type CabinetMe } from "@/lib/api";
import { PageLoader, Button } from "@/components/ui";
import { Dashboard } from "@/screens/Dashboard";
import { Referrals } from "@/screens/Referrals";
import { Finance } from "@/screens/Finance";
import { Bots } from "@/screens/Bots";

type Boot = "loading" | "authed" | "anon";
type Tab = "dashboard" | "bots" | "referrals" | "finance";

// Одноразовый обмен login-токена из ссылки бота (?token=) на cookie-сессию. Токен из URL
// убираем сразу (history.replace), чтобы он не остался в истории/по кнопке «назад».
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

// Если кабинет открыт как Telegram Mini App (кнопка-WebApp из бота) — telegram-web-app.js
// подставит window.Telegram.WebApp.initData. Грузится async, поэтому коротко ждём.
async function tgInitData(): Promise<string> {
  for (let i = 0; i < 20; i++) {
    const d = (window as any).Telegram?.WebApp?.initData;
    if (typeof d === "string" && d.length > 0) return d;
    if ((window as any).Telegram?.WebApp && i > 2) return ""; // SDK есть, но не WebApp-контекст
    await sleep(100);
  }
  return "";
}

async function bootstrap(): Promise<CabinetMe | null> {
  const url = new URL(window.location.href);
  const token = url.searchParams.get("token");
  if (token && (url.pathname === "/auth" || url.pathname === "/auth/")) {
    // login-токен одноразовый, но гасится только при УСПЕШНом обмене; на сетевой сбое/5xx он
    // остаётся валидным — поэтому повторяем несколько раз, прежде чем сдаться. Явный 401
    // (протухший/использованный токен) не ретраим.
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const me = await cabinetApi.exchange(token);
        window.history.replaceState({}, "", "/");
        return me;
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) break;
        if (attempt < 2) await sleep(1200);
      }
    }
    window.history.replaceState({}, "", "/");
    return null;
  }
  // Открыт из бота как Mini App → мгновенный вход по initData (без ссылок/токенов).
  const initData = await tgInitData();
  if (initData) {
    try {
      return await cabinetApi.telegram(initData);
    } catch {
      /* нет доступа/невалидно — попробуем существующую cookie-сессию ниже */
    }
  }
  try {
    return await cabinetApi.me();
  } catch {
    return null;
  }
}

export default function App() {
  const [boot, setBoot] = useState<Boot>("loading");
  const [me, setMe] = useState<CabinetMe | null>(null);
  const [tab, setTab] = useState<Tab>("dashboard");

  useEffect(() => {
    void bootstrap().then((m) => {
      setMe(m);
      setBoot(m ? "authed" : "anon");
    });
  }, []);

  if (boot === "loading") return <PageLoader />;
  if (boot === "anon" || !me) return <Login />;

  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col">
      <Header phone={me.phone} onLogout={() => void cabinetApi.logout().finally(() => setBoot("anon"))} />
      <main className="flex-1 px-4 pb-24 pt-3">
        {tab === "dashboard" && <Dashboard />}
        {tab === "bots" && <Bots />}
        {tab === "referrals" && <Referrals />}
        {tab === "finance" && <Finance />}
      </main>
      <TabBar tab={tab} onTab={setTab} />
    </div>
  );
}

function Header({ phone, onLogout }: { phone: string | null; onLogout: () => void }) {
  return (
    <header className="flex items-center justify-between px-4 pt-4">
      <div>
        <div className="text-lg font-bold tracking-tight">
          TT<span className="text-brand">EX</span> · партнёр
        </div>
        {phone && <div className="text-xs text-white/40">{phone}</div>}
      </div>
      <button onClick={onLogout} className="text-xs text-white/40 hover:text-white/70">
        Выйти
      </button>
    </header>
  );
}

function TabBar({ tab, onTab }: { tab: Tab; onTab: (t: Tab) => void }) {
  const items: { key: Tab; label: string; icon: string }[] = [
    { key: "dashboard", label: "Обзор", icon: "◎" },
    { key: "bots", label: "Боты", icon: "🤖" },
    { key: "referrals", label: "Клиенты", icon: "◍" },
    { key: "finance", label: "Финансы", icon: "▤" },
  ];
  return (
    <nav className="fixed inset-x-0 bottom-0 mx-auto max-w-md border-t border-white/5 bg-surface-base/95 backdrop-blur">
      <div className="grid grid-cols-4">
        {items.map((it) => (
          <button
            key={it.key}
            onClick={() => onTab(it.key)}
            className={`flex flex-col items-center gap-0.5 py-3 text-xs ${
              tab === it.key ? "text-brand" : "text-white/45"
            }`}
          >
            <span className="text-base leading-none">{it.icon}</span>
            {it.label}
          </button>
        ))}
      </div>
    </nav>
  );
}

function Login() {
  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col items-center justify-center px-6 text-center">
      <div className="text-2xl font-bold tracking-tight">
        TT<span className="text-brand">EX</span> · кабинет партнёра
      </div>
      <p className="mt-3 text-sm text-white/50">
        Откройте кабинет из Telegram-бота: команда{" "}
        <span className="font-mono text-white/80">/cabinet</span> → кнопка{" "}
        <span className="text-white/80">«Открыть кабинет»</span>. Вход мгновенный, без ссылок.
      </p>
      <Button
        variant="ghost"
        className="mt-6"
        onClick={() => window.location.reload()}
      >
        Обновить
      </Button>
    </div>
  );
}
